import React, { ComponentProps, forwardRef, memo, useCallback } from 'react'

import { css } from '@emotion/react'
import styled from '@emotion/styled'

import { PropsWithTheme } from '@surglogs-app/ui.theme'

import { Box } from './Box'
import { FlexBox } from './FlexBox'
import { TextBold } from './Text'

type SwitchVariant = 'default' | 'toggle'

interface SwitchStyleProps {
  variant: SwitchVariant
  disabled?: boolean
  checked?: boolean
}

const SWITCH_WIDTH = 40
const TOGGLE_WIDTH = 86
const PADDING = 3

const translateElement = (width: number, condition: boolean) => {
  return css`
    transform: translate(
      ${condition ? `calc(${width - 2 * PADDING}px - 100%)` : '0%'}
    );
  `
}

const getContainerVariantStyles = ({
  theme,
  variant,
  checked,
  disabled,
}: PropsWithTheme<SwitchStyleProps>) => {
  if (variant === 'toggle') {
    return css`
      width: ${TOGGLE_WIDTH}px;
      background-color: ${theme.ui.colors[checked ? 'green.100' : 'red.75']};

      ${disabled &&
      css`
        background-color: ${theme.ui.colors[checked ? 'green.50' : 'red.50']};
      `}
    `
  }

  return css`
    background-color: ${theme.ui.colors[checked ? 'blue.100' : 'navy.25']};
    width: ${SWITCH_WIDTH}px;
  `
}

export const SwitchContainer = styled(FlexBox)<SwitchStyleProps>`
  align-items: center;
  position: relative;
  overflow: hidden;
  height: ${({ theme }) => theme.ui.spacings.lg};
  cursor: pointer;
  border-radius: ${({ theme }) => theme.ui.spacings.lg};
  padding: ${PADDING}px;
  user-select: none;

  ${getContainerVariantStyles}

  ${({ disabled }) =>
    disabled
      ? css`
          pointer-events: none;
          cursor: default;
        `
      : ``};
`

const getCircleVariantStyles = ({
  theme,
  checked,
  variant,
  disabled,
}: PropsWithTheme<SwitchStyleProps>) => {
  if (variant === 'toggle') {
    return css`
      ${translateElement(TOGGLE_WIDTH, !!checked)}

      ${disabled &&
      css`
        background-color: ${theme.ui.colors[checked ? 'green.25' : 'red.25']};
      `}
    `
  }

  return css`
    ${translateElement(SWITCH_WIDTH, !!checked)}

    ${disabled &&
    css`
      background-color: ${theme.ui.colors[checked ? 'blue.25' : 'navy.10']};
    `}
  `
}

const SwitchCircle = styled(Box)<SwitchStyleProps>`
  position: absolute;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  transition: transform 200ms;
  background-color: ${({ theme }) => theme.ui.colors['white.100']};

  ${getCircleVariantStyles}
`

const ToggleLabel = styled(TextBold)<Omit<SwitchStyleProps, 'variant'>>`
  transition: transform 200ms;
  ${({ checked }) => translateElement(TOGGLE_WIDTH, !checked)};

  ${({ theme, disabled, checked }) =>
    disabled &&
    css`
      color: ${theme.ui.colors[checked ? 'green.25' : 'red.25']};
    `};
`

interface SwitchProps
  extends Omit<
    ComponentProps<typeof SwitchContainer>,
    'onChange' | 'checked' | 'variant'
  > {
  value?: boolean | null
  onChange?: (value: boolean) => void
  variant?: SwitchVariant
  onLabel?: string
  offLabel?: string
}

export const Switch = memo(
  forwardRef<HTMLDivElement, SwitchProps>(
    (
      {
        value,
        onChange,
        disabled,
        variant = 'default',
        onLabel,
        offLabel,
        ...rest
      },
      ref,
    ) => {
      const checked = !!value
      const onClick = useCallback(
        () => typeof onChange === 'function' && onChange(!value),
        [onChange, value],
      )

      return (
        <SwitchContainer
          {...{
            role: 'switch',
            onClick,
            ref,
            disabled,
            'aria-checked': checked,
            'aria-readonly': disabled,
            checked,
            variant,
            ...rest,
          }}>
          {variant === 'toggle' && onLabel && offLabel ? (
            <ToggleLabel
              {...{
                tt: 'uppercase',
                variant: 'secondary',
                px: 'sm',
                size: 'xxs',
                checked,
                disabled,
              }}>
              {checked ? onLabel : offLabel}
            </ToggleLabel>
          ) : null}
          <SwitchCircle {...{ checked, disabled, variant }} />
        </SwitchContainer>
      )
    },
  ),
)
